import './CopAssetDetails.scss'

import { useEffect, useRef, useState } from 'react'

import { AssetsPermissionsV2 } from '../../constants/PermissionsV2'
import { emitReportDataIssueOpen } from '../../constants/events'
import { COP_COMMON_ERROR_MESSAGES } from '../../constants/common-error-messages'
import PermissionAccessGuard from '../Shared/PermissionAccessGuard/PermissionAccessGuard'
import { useHasPermissionV2 } from '../Shared/PermissionAccessGuard/hooks/useHasPermissionV2'
import { AssetDetails, type ButtonConfig } from './asset-details'
import { AssetDocuments } from './asset-documents'
import { AssetMap } from './asset-map'
import { ToastService } from '../../services/toast.service'
import { fetchAssetDetails, getAssetIdFromUrl, type MicroSCADAAssetDto } from './services/CopAssetDetailsService'
export type { ButtonConfig }
export type { DetailField } from './asset-details'

export type CopAssetDetailsProps = {
  readonly enableButton?: string
  readonly buttons?: string
  readonly enableMap?: string
  readonly detailFields?: string
  readonly enableDocument?: string
  readonly documentSections?: string
  readonly sectionTitle?: string
}

function decodeHtmlEntities(str: string): string {
  const textarea = document.createElement('textarea')
  textarea.innerHTML = str
  return textarea.value
}

function parseJsonProp<T>(prop: string | undefined, defaultValue: T): T {
  if (!prop) return defaultValue
  try {
    return JSON.parse(decodeHtmlEntities(prop)) as T
  } catch {
    return defaultValue
  }
}

export default function CopAssetDetails(props: Readonly<CopAssetDetailsProps>) {
  const showMap = props.enableMap !== 'false'
  const showDocument = props.enableDocument !== 'false'
  const showButtons = props.enableButton !== 'false'

  const buttons = parseJsonProp<ButtonConfig[]>(props.buttons, [])

  const [data, setData] = useState<MicroSCADAAssetDto | null>(null)
  const [loading, setLoading] = useState(true)
  const loadedRef = useRef(false)

  const viewDetailsState = useHasPermissionV2(AssetsPermissionsV2.ViewDetails)

  const openReportDataIssue = () => {
    const assetContextName = data?.id ? String(data.id) : data?.name

    emitReportDataIssueOpen({
      contextType: 'asset',
      contextId: data?.id,
      contextName: assetContextName,
    })
  }

  useEffect(() => {
    if (viewDetailsState !== 'permitted') return
    if (loadedRef.current) return
    loadedRef.current = true
    let cancelled = false

    const assetId = getAssetIdFromUrl()
    if (!assetId) {
      setLoading(false)
      return
    }

    const breadcrumbLabel = document.querySelector<HTMLElement>('.cmp-breadcrumb__item--active [itemprop="name"]')
    if (breadcrumbLabel) breadcrumbLabel.textContent = assetId

    fetchAssetDetails(assetId)
      .then(result => {
        if (cancelled) return
        setData(result ?? null)
      })
      .catch(err => {
        const fallbackMessage = COP_COMMON_ERROR_MESSAGES['api-error'].message.replace(
          '{{action}}',
          COP_COMMON_ERROR_MESSAGES['api-error'].action['assets-loading'],
        )
        const message = err instanceof Error && err.message ? err.message : fallbackMessage
        ToastService.error(message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [viewDetailsState])

  if (viewDetailsState === 'loading' || (viewDetailsState === 'permitted' && loading)) {
    return (
      <div className="cop-asset-details cop-asset-details--loading">
        <div className="cop-asset-details__loader">Loading...</div>
      </div>
    )
  }

  if (viewDetailsState === 'denied') return null

  const renderAssetDetails = (viewMode: 'all' | 'header' | 'rows') => (
    <AssetDetails
      data={data as unknown as Record<string, unknown> | null}
      detailFields={props.detailFields}
      showButtons={showButtons}
      buttons={buttons}
      onReportIssueClick={openReportDataIssue}
      viewMode={viewMode}
    />
  )

  return (
    <div className="cop-asset-details">
      <div className="cop-asset-details__layout">
        <div className="cop-asset-details__mobile-header">
          {renderAssetDetails('header')}
        </div>

        <div className="cop-asset-details__desktop-details">
          {renderAssetDetails('all')}
        </div>

        {showMap && data?.latitude != null && data?.longitude != null && (
          <AssetMap latitude={data.latitude} longitude={data.longitude} />
        )}

        <div className="cop-asset-details__mobile-details">
          {renderAssetDetails('rows')}
        </div>
      </div>

      {showDocument && (
        <PermissionAccessGuard permission={AssetsPermissionsV2.ViewDetailsDocuments}>
          <AssetDocuments documentSections={props.documentSections} sectionTitle={props.sectionTitle} contentId={data?.contentId} />
        </PermissionAccessGuard>
      )}
    </div>
  )
}
